import React from 'react';
import styled from 'styled-components';
import { ILeg, ILegSelectionRunner } from '@williamhill/rosi-ui-shared/src/types';
import { mapLegStatus } from '@williamhill/rosi-ui-shared/src/lib/bet-status';
import { formatDateOnly } from '@williamhill/rosi-ui-shared/src/lib/time';
import { ResultIcon } from './bet-legs';
import transform from './transform-legs';
import dayjs from 'dayjs';
import { Block } from '../typography';
import { iconsByResultType } from './';

interface IProps {
  legs: Leg[];
  getLocalDateTime: (date: Date) => Date;
}

export type Leg = {
  event: Pick<ILeg['event'], 'name' | 'raceNumber'>;
  selection: { runners?: Pick<ILegSelectionRunner, 'number'>[] | null };
  result: Parameters<typeof mapLegStatus>[0];
  eventStartTime: string;
};

const renderLegs = ({ legs, getLocalDateTime }: IProps) =>
  legs.map((leg: Leg, i: number) => {
    const { selection, event, result, eventStartTime } = leg;
    const { label } = mapLegStatus(result);
    const formattedDate = formatDateOnly(getLocalDateTime(dayjs(eventStartTime).toDate()));

    return (
      <Tr key={`bet-finder-leg-${i}`} className="qa-bet-finder-bet-legs-table">
        <Cell>{event.name}</Cell>
        <Cell>{event.raceNumber}</Cell>
        <Cell>{selection.runners?.map(item => <div key={item.number}>{item.number}</div>)}</Cell>
        <Cell>{formattedDate}</Cell>
        <Cell justify="flex-end">
          <ResultIcon icon={iconsByResultType[result?.type ?? '']}>
            <span>{label}</span>
          </ResultIcon>
        </Cell>
      </Tr>
    );
  });

const ParimutuelBetLegs = (props: IProps) => {
  const legs = transform(props.legs);
  return (
    <BetLegsBlock className="qa-bet-finder-bet-legs-table" size="lg">
      <THead className="qa-bet-finder-header">
        <Cell className="qa-bet-finder-track">
          <span>Track</span>
        </Cell>
        <Cell className="qa-bet-finder-race">
          <span>Race</span>
        </Cell>
        <Cell className="qa-bet-finder-runner">
          <span>Runner(s)</span>
        </Cell>
        <Cell className="qa-bet-finder-runner">
          <span>Date</span>
        </Cell>
        <Cell className="qa-bet-finder-result" justify="flex-end">
          <span>Result</span>
        </Cell>
      </THead>
      <BetLegsBlockBody className="qa-bet-finder-body">{renderLegs({ ...props, legs })}</BetLegsBlockBody>
    </BetLegsBlock>
  );
};

const BetLegsBlock = styled(Block)`
  background-color: ${({ theme }) => theme.colors.white};
  text-align: left;
`;

const BetLegsBlockBody = styled.div`
  overflow-y: scroll;
`;

const Tr = styled(Block).attrs(() => ({ as: 'tr', weight: 400 }))`
  display: grid;
  grid-template-columns: 2fr 2fr 2fr 2fr 4fr;
`;

const THead = styled(Tr).attrs(() => ({ weight: 700 }))`
  background-color: ${({ theme }) => theme.colors.supporting.accent100};
`;

const Cell = styled.div<{ justify?: string; paddingLeft?: string }>`
  align-items: center;
  display: flex;
  padding: ${({ paddingLeft = '2rem' }) => {
    return `1rem 2rem 1rem ${paddingLeft}`;
  }};
  ${({ justify }) => justify && `justify-content: ${justify};`};
`;

export default ParimutuelBetLegs;
